import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { t, L } from "../lib/translations";
import {
  X, Trash2, ShoppingBag, ArrowRight, Coins,
  Users, Heart, MessageSquare, Package, Wallet, Eye,
} from "lucide-react";

const fmtIP = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const CATEGORY = {
  followers:   { Icon: Users,         color: "#833AB4" },
  likes:       { Icon: Heart,         color: "#E1306C" },
  comments:    { Icon: MessageSquare, color: "#405DE6" },
  views:       { Icon: Eye,           color: "#0EA5E9" },
  story_views: { Icon: Eye,           color: "#8B5CF6" },
  story_likes: { Icon: Heart,         color: "#EC4899" },
  bundle:      { Icon: Package,       color: "#F77737" },
};

export function CartSidebar() {
  const { items, sidebarOpen, setSidebarOpen, removeItem } = useCart();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const total     = items.reduce((s, i) => s + (i.total_ipoints || 0), 0);
  const balance   = user?.ipoints || 0;
  const canAfford = balance >= total && items.length > 0;
  const deficit   = total - balance;

  const go = (path) => { setSidebarOpen(false); navigate(path); };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cart-sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Panel */}
      <div
        className={`cart-sidebar ${sidebarOpen ? "open" : ""} flex flex-col`}
        data-testid="cart-sidebar"
        style={{ background: "var(--bg-primary)" }}
      >

        {/* ── Header ── */}
        <div className="flex-shrink-0 relative overflow-hidden flex items-center justify-between px-5 py-4"
          style={{ background: "linear-gradient(135deg, #E1306C 0%, #833AB4 100%)" }}>
          {/* Watermark */}
          <div className="absolute -end-5 -bottom-6 opacity-[0.15] pointer-events-none" style={{ color: "#fff" }}>
            <ShoppingBag style={{ width: 90, height: 90 }} />
          </div>
          <div className="relative z-10">
            <p className="text-[11px] font-semibold uppercase tracking-widest mb-0.5"
              style={{ color: "rgba(255,255,255,0.75)" }}>
              {t("c.CartSidebar.your_cart", lang)}
            </p>
            <h3 className="text-lg font-black font-outfit leading-none" style={{ color: "#fff" }}>
              {items.length > 0
                ? `${items.length} ${L(lang, items.length === 1 ? "item" : "items", items.length === 1 ? "товар" : "товаров", "פריטים")}`
                : t("c.CartSidebar.empty", lang)}
            </h3>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="relative z-10 w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:bg-white/30"
            style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
            data-testid="close-cart-sidebar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Items ── */}
        <div className="flex-1 overflow-y-auto sidebar-scroll py-3 px-4 space-y-2">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #E1306C 0%, #833AB4 100%)",
                  boxShadow: "0 8px 24px rgba(225,48,108,0.4)",
                }}>
                <ShoppingBag className="w-7 h-7" style={{ color: "#fff" }} />
              </div>
              <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                {t("c.CartSidebar.add_services_from_the_shop", lang)}
              </p>
              <button
                onClick={() => go("/shop")}
                className="text-xs font-bold px-5 py-2.5 rounded-full transition-all hover:opacity-90 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #E1306C, #833AB4)",
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(225,48,108,0.4)",
                }}
              >
                {t("nav.shop", lang)}
              </button>
            </div>
          ) : (
            items.map((item) => {
              const cat = item.product?.category;
              const c = CATEGORY[cat] || CATEGORY.bundle;
              const Icon = c.Icon;
              const isBundle = cat === "bundle";
              const target = item.target_url || item.instagram_link;
              const commentCount = String(item.custom_comments || "").split("\n").filter(Boolean).length;

              return (
                <div
                  key={item.cart_item_id}
                  className="relative rounded-2xl overflow-hidden group"
                  style={{
                    background: `linear-gradient(135deg, ${c.color} 0%, ${c.color}BB 100%)`,
                    boxShadow: `0 6px 18px ${c.color}45`,
                  }}
                  data-testid={`sidebar-item-${item.cart_item_id}`}
                >
                  {/* Legibility overlay */}
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.32) 100%)" }} />

                  {/* Watermark icon */}
                  <div className="absolute -bottom-3 -end-3 opacity-[0.15] pointer-events-none select-none" style={{ color: "#fff" }}>
                    <Icon style={{ width: 70, height: 70 }} />
                  </div>

                  <div className="relative z-10 p-3.5">
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(255,255,255,0.2)" }}>
                        <Icon style={{ width: 16, height: 16, color: "#fff" }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Name + delete */}
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[13px] font-bold leading-snug flex-1 truncate"
                            style={{ color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>
                            {item.product?.name?.[lang] || item.product?.name?.en}
                          </p>
                          <button
                            onClick={() => removeItem(item.cart_item_id)}
                            aria-label={`Remove ${item.product?.name?.[lang] || item.product?.name?.en || 'item'}`}
                            className="w-6 h-6 rounded-lg flex items-center justify-center opacity-50 hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0"
                            style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
                            data-testid={`sidebar-remove-${item.cart_item_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Details */}
                        <div className="mt-1 space-y-0.5">
                          {isBundle ? (
                            <>
                              <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.75)" }}>
                                👤 {item.instagram_link}
                              </p>
                              {item.instagram_link_2 && (
                                <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.75)" }}>
                                  📸 {item.instagram_link_2}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              {target && (
                                <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.75)" }}>{target}</p>
                              )}
                              <div className="flex items-center gap-3">
                                {item.quantity > 0 && (
                                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
                                    ×{(item.quantity || 0).toLocaleString()}
                                  </span>
                                )}
                                {commentCount > 0 && (
                                  <span className="flex items-center gap-1 text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
                                    <MessageSquare className="w-3 h-3" />{commentCount}
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mt-3 pt-2.5 flex items-center justify-end"
                      style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                      <span className="flex items-center gap-1.5 font-black font-outfit text-sm" style={{ color: "#fff" }}>
                        <Coins className="w-3.5 h-3.5" />
                        {fmtIP(item.total_ipoints)}
                        <span className="text-[11px] font-normal" style={{ color: "rgba(255,255,255,0.7)" }}>iP</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Footer ── */}
        {items.length > 0 && (
          <div className="flex-shrink-0 px-4 pb-5 pt-3 space-y-3">

            {/* Totals card */}
            <div className="relative overflow-hidden rounded-2xl p-3.5 space-y-2.5"
              style={{
                background: "linear-gradient(135deg, #F77737 0%, #E1306C 100%)",
                boxShadow: "0 6px 20px rgba(247,119,55,0.45)",
              }}>
              <div className="absolute -end-4 -bottom-4 opacity-[0.12] pointer-events-none" style={{ color: "#fff" }}>
                <Coins style={{ width: 80, height: 80 }} />
              </div>

              <div className="relative z-10 flex items-center justify-between">
                <span className="text-xs font-semibold" style={{ color: "rgba(255,255,255,0.8)" }}>
                  {t("cart.total", lang)}
                </span>
                <span className="flex items-center gap-1.5 font-black font-outfit text-lg" style={{ color: "#fff" }}>
                  <Coins className="w-4 h-4" />
                  {fmtIP(total)}
                  <span className="text-xs font-normal opacity-75">iP</span>
                </span>
              </div>

              {user && (
                <>
                  <div className="relative z-10 flex items-center justify-between pt-2.5"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>
                      <Wallet className="w-3.5 h-3.5" />
                      {t("cart.your_balance", lang)}
                    </span>
                    <span className="font-bold font-outfit text-sm" style={{ color: "#fff" }}>
                      {fmtIP(balance)} iP
                    </span>
                  </div>

                  <div className="relative z-10 flex items-center justify-between pt-2.5"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>
                      {t("cart.after_purchase", lang)}
                    </span>
                    <span className="font-bold font-outfit text-sm"
                      style={{ color: canAfford ? "#86efac" : "#fca5a5" }}>
                      {fmtIP(balance - total)} iP
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Not enough */}
            {user && !canAfford && (
              <div className="relative overflow-hidden flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl"
                style={{
                  background: "linear-gradient(135deg, rgba(225,48,108,0.15) 0%, rgba(131,58,180,0.15) 100%)",
                  border: "1px solid rgba(225,48,108,0.3)",
                }}>
                <span className="text-xs font-semibold" style={{ color: "#E1306C" }}>
                  {L(lang,
                    `Need ${fmtIP(deficit)} more iPoints`,
                    `Не хватает ${fmtIP(deficit)} iPoints`,
                    `חסרים ${fmtIP(deficit)} iPoints`
                  )}
                </span>
                <button
                  onClick={() => { setSidebarOpen(false); navigate(`/topup?needed_ip=${Math.round(Math.max(0, deficit) * 100) / 100}`); }}
                  className="text-[11px] font-bold px-3 py-1 rounded-full flex-shrink-0 transition-opacity hover:opacity-80"
                  style={{ background: "linear-gradient(135deg,#E1306C,#833AB4)", color: "#fff" }}
                >
                  {t("Shop.top_up", lang)}
                </button>
              </div>
            )}

            {/* Checkout */}
            <button
              onClick={() => go("/cart")}
              className="w-full h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #E1306C, #833AB4)",
                color: "#fff",
                boxShadow: "0 4px 20px rgba(225,48,108,0.45)",
              }}
              data-testid="sidebar-checkout-btn"
            >
              {t("cart.checkout", lang)}
              <ArrowRight className="w-4 h-4 rtl-flip" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
